
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSite } from "./siteContext";
import { useAuth } from "./authContext";
import { getUserCart, updateUserCart } from "../services/cartService";
import { trackEvent } from "../utilities/analyticsService";
import { getEffectiveProductDisplay } from "../utilities/replacementProduct";
import { getMinimumSalesOrderQuantity, getMultipleSalesOrderQuantity, normalizeOrderQuantity, parseOrderQuantity } from "../utilities/orderQuantity";

export const CartContext = createContext();

// Helper function to get product ID, handling both 'ProductID' and 'product_id'
const getNormalizedProductId = (item) => item?.ProductID || item?.product_id;
const getNormalizedMachineId = (item) =>
  item?.MachineID || item?.machine_id || item?.machineId || null;
const getCartQuantity = (item) => Number(item?.Quantity ?? item?.quantity ?? 1) || 1;
const getCartSite = (item, fallbackSite = "") => item?.Site || item?.site_number || fallbackSite;
const isCartActionMachineMatch = (item, machineId) => {
  const itemMachineId = getNormalizedMachineId(item);
  return !machineId || !itemMachineId || Number(itemMachineId) === Number(machineId);
};
const isSameCartLine = (left, right, fallbackSite = "") => {
  if (String(getNormalizedProductId(left)) !== String(getNormalizedProductId(right))) return false;
  if (String(getCartSite(left, fallbackSite)) !== String(getCartSite(right, fallbackSite))) return false;
  if (
    left?.replaced_from_product_id ||
    right?.replaced_from_product_id ||
    left?.replaced_by ||
    right?.replaced_by ||
    left?.replacement_product_number ||
    right?.replacement_product_number
  ) {
    return true;
  }
  const leftMachineId = getNormalizedMachineId(left);
  const rightMachineId = getNormalizedMachineId(right);
  if (leftMachineId && rightMachineId) {
    return Number(leftMachineId) === Number(rightMachineId);
  }
  return !leftMachineId && !rightMachineId;
};
const getCartPageMetadata = () => {
  if (typeof window === "undefined") return {};
  return {
    path: window.location.pathname || "/",
    page_path: window.location.pathname || "/",
    search: window.location.search || "",
    url: window.location.href || "",
  };
};

const trackCartReady = (metadata = {}) => {
  trackEvent("funnel_cart_ready", {
    ...getCartPageMetadata(),
    action_category: "cart",
    funnel_name: "order_conversion",
    funnel_step: "cart_ready",
    step: "cart_ready",
    ...metadata,
  });
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [userId, setUserId] = useState(null);
  const { selectedSite, selectedSiteCurrency } = useSite();
  const { user, canAddToCart } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const debounceTimer = useRef(null);
  const cartItemsRef = useRef([]);
  const saveRequestSeq = useRef(0);

  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  const cartCount = useMemo(() => {
    if (!cartItems || cartItems.length === 0) {
      return 0;
    }
    const filteredItems = cartItems.filter(
      (item) => String(getCartSite(item)) === String(selectedSite)
    );
    return filteredItems.reduce(
      (total, item) => total + (parseOrderQuantity(item.Quantity) || 0),
      0
    );
  }, [cartItems, selectedSite]);

  const fetchCart = async (userId) => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const response = await getUserCart(userId);
      const serverCart = Array.isArray(response?.data) ? response.data : [];
      cartItemsRef.current = serverCart;
      setCartItems(serverCart);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    if (user) {
      setUserId(user.user_id);
    }
  }, [user]);

  useEffect(() => {
    if (userId) {
      fetchCart(userId);
    }
  }, [userId]);

  const refreshCart = useCallback(() => {
    if (userId) {
      fetchCart(userId);
    }
  }, [userId]);

  const updateCart = async (newCartItems) => {
    cartItemsRef.current = newCartItems;
    setCartItems(newCartItems); // Optimistic UI update

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    const requestSeq = ++saveRequestSeq.current;
    debounceTimer.current = setTimeout(async () => {
      if (userId) {
        try {
          const response = await updateUserCart(userId, newCartItems);
          if (
            requestSeq === saveRequestSeq.current &&
            Array.isArray(response?.data)
          ) {
            cartItemsRef.current = response.data;
            setCartItems(response.data);
          }
        } catch (error) {
          console.error("Error updating cart on the server:", error);
          // Optional: Revert to the server state if the update fails
          if (requestSeq === saveRequestSeq.current) {
            fetchCart(userId);
          }
        }
      }
    }, 500); // 500ms debounce delay
  };

  const addToCart = async (product) => {
    if (!canAddToCart()) return;
    const currentCartItems = cartItemsRef.current;
    const effectiveProduct = getEffectiveProductDisplay(product, {
      machineId: getNormalizedMachineId(product),
      currency: selectedSiteCurrency,
    });
    const productToAdd = {
      ...effectiveProduct,
      minimum_sales_order_quantity:
        effectiveProduct.minimum_sales_order_quantity ??
        getMinimumSalesOrderQuantity(effectiveProduct),
      multiple_sales_order_quantity: getMultipleSalesOrderQuantity(effectiveProduct),
      Quantity: normalizeOrderQuantity(effectiveProduct.Quantity, effectiveProduct),
      Site: getCartSite(effectiveProduct, selectedSite),
    };

    let existingProductIndex = currentCartItems.findIndex((item) =>
      isSameCartLine(item, productToAdd, selectedSite)
    );
    if (existingProductIndex < 0 && getNormalizedMachineId(productToAdd)) {
      existingProductIndex = currentCartItems.findIndex(
        (item) =>
          getNormalizedProductId(item) === getNormalizedProductId(productToAdd) &&
          getCartSite(item, selectedSite) === getCartSite(productToAdd, selectedSite) &&
          !getNormalizedMachineId(item)
      );
    }
    let newCartItems;
    if (existingProductIndex > -1) {
      newCartItems = currentCartItems.map((item, index) =>
        index === existingProductIndex
          ? {
            ...item,
            ...productToAdd,
            Quantity:
              (parseOrderQuantity(item.Quantity) || 0) + (parseOrderQuantity(productToAdd.Quantity) || 0),
          }
          : item
      );
    } else {
      newCartItems = [...currentCartItems, productToAdd];
    }
    await updateCart(newCartItems);
    trackCartReady({
      element_label: "Add to Cart",
      product_id: getNormalizedProductId(productToAdd),
      product_count: 1,
      quantity: getCartQuantity(productToAdd),
      site_id: selectedSite,
      machine_id: getNormalizedMachineId(productToAdd),
    });
  };

  const addMultipleToCart = async (products) => {
    if (!canAddToCart()) return;
    let newCartItems = [...cartItemsRef.current];
    products.forEach((product) => {
      const effectiveProduct = getEffectiveProductDisplay(product, {
        machineId: getNormalizedMachineId(product),
        currency: selectedSiteCurrency,
      });
      const productQuantity = normalizeOrderQuantity(effectiveProduct.Quantity ?? 1, effectiveProduct);
      const normalizedProduct = {
        ...effectiveProduct,
        minimum_sales_order_quantity:
          effectiveProduct.minimum_sales_order_quantity ??
          getMinimumSalesOrderQuantity(effectiveProduct),
        multiple_sales_order_quantity: getMultipleSalesOrderQuantity(effectiveProduct),
        Site: getCartSite(effectiveProduct, selectedSite),
      };
      let existingProductIndex = newCartItems.findIndex((item) =>
        isSameCartLine(item, normalizedProduct, selectedSite)
      );
      if (existingProductIndex < 0 && getNormalizedMachineId(normalizedProduct)) {
        existingProductIndex = newCartItems.findIndex(
          (item) =>
            getNormalizedProductId(item) === getNormalizedProductId(normalizedProduct) &&
            getCartSite(item, selectedSite) === getCartSite(normalizedProduct, selectedSite) &&
            !getNormalizedMachineId(item)
        );
      }

      if (existingProductIndex > -1) {
        newCartItems[existingProductIndex] = {
          ...newCartItems[existingProductIndex],
          ...normalizedProduct,
          Quantity: Number(newCartItems[existingProductIndex].Quantity || 0) + Number(productQuantity),
        };
      } else {
        newCartItems.push({ ...normalizedProduct, Quantity: productQuantity });
      }
    });
    await updateCart(newCartItems);
    if (products.length > 0) {
      trackCartReady({
        element_label: "Add Multiple to Cart",
        product_count: products.length,
        product_ids: products.map(getNormalizedProductId).filter(Boolean).slice(0, 20).join(","),
        quantity: products.reduce((total, product) => total + getCartQuantity(product), 0),
        site_id: selectedSite,
      });
    }
  };

  const removeFromCart = async (productId, machineId = null) => {
    const newCartItems = cartItemsRef.current.filter(
      (item) =>
        !(
          String(getNormalizedProductId(item)) === String(productId) &&
          getCartSite(item, selectedSite) === selectedSite &&
          isCartActionMachineMatch(item, machineId)
        )
    );
    await updateCart(newCartItems);
  };

  const clearCart = async () => {
    const newCartItems = cartItemsRef.current.filter(
      (item) => String(getCartSite(item, selectedSite)) !== String(selectedSite)
    );

    cartItemsRef.current = newCartItems;
    setCartItems(newCartItems);

    await updateCart(newCartItems);
  };
  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        addToCart,
        removeFromCart,
        clearCart,
        updateCart,
        addMultipleToCart,
        isLoading,
        isInitialized,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
